import React from 'react';
import {
  faFileLines,
  faPause,
  faPlay,
  faSpinnerThird,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Text, Tooltip } from '@cowbell/ui';
import type { AudioPlaybackStatus } from '../hooks/useAudioPlayback';
import type {
  AttachmentTranscript,
  TranscriptState,
} from '../hooks/useAttachmentTranscript';
import { formatPlaybackTime } from '../utils/audioAttachmentUtils';
import classes from './AttachmentChip.module.css';

export interface AudioAttachmentChipProps {
  readonly fileName: string;
  readonly sizeLabel: string;
  readonly status: AudioPlaybackStatus;
  readonly currentTime: number;
  readonly duration: number;
  readonly onToggle: () => void;
  readonly onSeek: (seconds: number) => void;
  readonly onDownload: () => void;
  /** Whether this recording has a transcript URL at all. */
  readonly hasTranscript: boolean;
  readonly transcriptOpen: boolean;
  readonly transcript: AttachmentTranscript;
  readonly onToggleTranscript: () => void;
}

const TRANSCRIPT_MESSAGE: Record<Exclude<TranscriptState, 'ready'>, string> = {
  loading: 'Loading transcript…',
  missing: 'Transcript not available yet.',
  error: 'Could not load the transcript.',
};

const TOGGLE_LABEL = {
  idle: 'Play',
  loading: 'Cancel loading',
  playing: 'Pause',
  paused: 'Play',
} as const;

const TOGGLE_ICON = {
  idle: faPlay,
  loading: faSpinnerThird,
  playing: faPause,
  paused: faPlay,
} as const;

/**
 * An attachment chip for a recording: play/pause in place of the paperclip,
 * elapsed time in place of the size once playback starts, and a seek bar
 * beneath. The filename still downloads the file. When the recording has a
 * transcript, a Transcript toggle opens it under the controls.
 */
export const AudioAttachmentChip = ({
  fileName,
  sizeLabel,
  status,
  currentTime,
  duration,
  onToggle,
  onSeek,
  onDownload,
  hasTranscript,
  transcriptOpen,
  transcript,
  onToggleTranscript,
}: AudioAttachmentChipProps) => {
  const started = status !== 'idle' && status !== 'loading';

  const handleDownloadKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onDownload();
    }
  };

  return (
    <Box
      role="group"
      aria-label={`Audio attachment ${fileName}`}
      className={[
        classes.attachmentChip,
        classes.audioChip,
        transcriptOpen ? classes.audioChipExpanded : '',
      ].join(' ')}
    >
      <Box className={classes.audioRow}>
        <button
          type="button"
          className={classes.playButton}
          onClick={onToggle}
          aria-label={`${TOGGLE_LABEL[status]} ${fileName}`}
          aria-busy={status === 'loading'}
        >
          <FontAwesomeIcon
            icon={TOGGLE_ICON[status]}
            spin={status === 'loading' || undefined}
            className={classes.playIcon}
          />
        </button>
        <Tooltip label={fileName}>
          <Box
            role="button"
            tabIndex={0}
            className={classes.audioDownload}
            onClick={onDownload}
            onKeyDown={handleDownloadKeyDown}
            aria-label={`Download ${fileName}`}
          >
            <Text className={classes.fileName}>{fileName}</Text>
            {started ? (
              <Text className={classes.sizeLabel} c="dimmed">
                {formatPlaybackTime(currentTime)} /{' '}
                {formatPlaybackTime(duration)}
              </Text>
            ) : (
              sizeLabel && (
                <Text className={classes.sizeLabel} c="dimmed">
                  {sizeLabel}
                </Text>
              )
            )}
          </Box>
        </Tooltip>
        {hasTranscript && (
          <button
            type="button"
            className={classes.transcriptToggle}
            onClick={onToggleTranscript}
            aria-expanded={transcriptOpen}
            aria-label={`${
              transcriptOpen ? 'Hide' : 'Show'
            } transcript of ${fileName}`}
          >
            <FontAwesomeIcon icon={faFileLines} className={classes.playIcon} />
            <span>Transcript</span>
          </button>
        )}
      </Box>
      {started && (
        <input
          type="range"
          className={classes.seekBar}
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={(event) => onSeek(Number(event.target.value))}
          aria-label={`Seek ${fileName}`}
          aria-valuetext={`${formatPlaybackTime(
            currentTime
          )} of ${formatPlaybackTime(duration)}`}
        />
      )}
      {transcriptOpen && (
        <Box
          className={classes.transcriptPanel}
          role="region"
          aria-label={`Transcript of ${fileName}`}
        >
          {transcript.state === 'ready' ? (
            <>
              <Text className={classes.transcriptCaption} c="dimmed">
                AI-cleaned transcript
              </Text>
              <Text className={classes.transcriptText}>{transcript.text}</Text>
            </>
          ) : (
            <Text className={classes.transcriptCaption} c="dimmed">
              {TRANSCRIPT_MESSAGE[transcript.state]}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};
